import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { TemplatePatch } from "@/pages/templates/templateApi";
import { RootState } from "@/store";

import {
  ActiveStatusEnum,
  DisplayedTemplateEnum,
  SaasType,
  TemplateType,
  TemplateTypeEnum,
} from "./templateUtils";

type TemplateStoreState = {
  page: number;
  templatesPerPage: number;
  count: number;
  search: string | null;
  language: string;
  saasSearch: SaasType | null;
  templateTypeSearch: TemplateTypeEnum;
  activeStatus: ActiveStatusEnum | null;
  displayOption: DisplayedTemplateEnum;
  bestPerformersOnly: boolean;
  templates: Array<TemplateType>;
};

const templateSlice = createSlice({
  name: "template",
  initialState: {
    page: 1,
    templatesPerPage: 25,
    count: 0,
    search: "",
    saasSearch: null,
    language: "all",
    templateTypeSearch: TemplateTypeEnum.ALL,
    activeStatus: ActiveStatusEnum.ACTIVE,
    displayOption: DisplayedTemplateEnum.ALL,
    bestPerformersOnly: false,
    templates: [],
  } as TemplateStoreState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload.page;
    },
    setTemplatesPerPage: (state, action) => {
      state.templatesPerPage = action.payload;
    },
    setCount: (state, action) => {
      state.count = action.payload;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload.search;
      state.page = 1;
    },
    setTemplates: (state, action) => {
      state.templates = action.payload;
    },
    updateTemplate: (state, action: PayloadAction<TemplatePatch>) => {
      const index = state.templates.findIndex(
        (t) => t.id === action.payload.id,
      );
      const newFieldsEntries = Object.entries(action.payload).filter(
        ([k, v]) => k !== "id" && v != null,
      );
      state.templates[index] = {
        ...state.templates[index],
        ...Object.fromEntries(newFieldsEntries),
      };
    },
    setSaasSearch: (state, action) => {
      state.saasSearch = action.payload.saas;
      state.page = 1;
    },
    setTemplateTypeSearch: (state, action) => {
      state.templateTypeSearch = action.payload;
      state.page = 1;
    },
    setDisplayOption: (state, action) => {
      state.displayOption = action.payload;
    },
    setActiveStatus: (state, action) => {
      state.activeStatus = action.payload;
      state.page = 1;
    },
    addTemplates: (state, action) => {
      state.templates.push(action.payload);
    },
    setBestPerformersOnly: (state, action) => {
      state.bestPerformersOnly = action.payload;
      state.page = 1;
    },
  },
});

export const {
  setPage,
  setTemplatesPerPage,
  setDisplayOption,
  setLanguage,
  addTemplates,
  setCount,
  setSearch,
  setSaasSearch,
  setTemplateTypeSearch,
  setActiveStatus,
  setBestPerformersOnly,
  setTemplates,
  updateTemplate,
} = templateSlice.actions;

export const selectPage = (state: RootState) => {
  return state.template.page;
};
export const selectLanguage = (state: RootState) => {
  return state.template.language;
};

export const selectTemplatesPerPage = (state: RootState) => {
  return state.template.templatesPerPage;
};

export const selectCount = (state: RootState) => {
  return state.template.count;
};

export const selectSearch = (state: RootState) => {
  return state.template.search;
};

export const selectSaasSearch = (state: RootState) => {
  return state.template.saasSearch;
};

export const selectTemplateTypeSearch = (state: RootState) => {
  return state.template.templateTypeSearch;
};

export const selectDisplayOption = (state: RootState) => {
  return state.template.displayOption;
};

export const selectTemplates = (state: RootState) => {
  return state.template.templates;
};

export const selectActiveStatus = (state: RootState) => {
  return state.template.activeStatus;
};

export const selectBestPerformersOnly = (state: RootState) => {
  return state.template.bestPerformersOnly;
};

export default templateSlice.reducer;
